import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import './Header.css';

const Header = ({ onMenuToggle }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { lang, setLang } = useLanguage();
  const navigate = useNavigate();

  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef(null);

  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  const displayName = user?.fullName || user?.username || 'User';
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    const handler = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setSettingsOpen(false);
      }
    };
    if (settingsOpen) {
      document.addEventListener('mousedown', handler);
    }
    return () => document.removeEventListener('mousedown', handler);
  }, [settingsOpen]);

  return (
    <header className="header">
      <div className="header-inner">
        <div className="header-left">
          {onMenuToggle && (
            <button
              className="header-menu-btn"
              onClick={onMenuToggle}
              aria-label="Toggle sidebar"
            >
              <i className="fas fa-bars"></i>
            </button>
          )}
          <span className="header-brand">EL ALSSON FINANCE | ACCOUNTING</span>
        </div>

        <div className="header-right">
          <span className="header-date">
            <i className="fas fa-calendar-alt" aria-hidden="true"></i>
            {today}
          </span>

          <div className="header-settings" ref={settingsRef}>
            <button
              className="header-settings-btn"
              onClick={() => setSettingsOpen((o) => !o)}
              title="Settings"
              aria-label="Settings"
              aria-expanded={settingsOpen}
            >
              <i className="fas fa-cog"></i>
            </button>

            {settingsOpen && (
              <div className="settings-panel" role="menu">
                <div className="settings-section">
                  <p className="settings-label">Appearance</p>
                  <div className="settings-options">
                    <button
                      className={`settings-opt${theme === 'light' ? ' active' : ''}`}
                      onClick={() => { if (theme !== 'light') toggleTheme(); }}
                    >
                      <i className="fas fa-sun"></i> Light
                    </button>
                    <button
                      className={`settings-opt${theme === 'dark' ? ' active' : ''}`}
                      onClick={() => { if (theme !== 'dark') toggleTheme(); }}
                    >
                      <i className="fas fa-moon"></i> Dark
                    </button>
                  </div>
                </div>

                <div className="settings-divider"></div>

                <div className="settings-section">
                  <p className="settings-label">Language</p>
                  <div className="settings-options">
                    <button
                      className={`settings-opt${lang === 'en' ? ' active' : ''}`}
                      onClick={() => setLang('en')}
                    >
                      English
                    </button>
                    <button
                      className={`settings-opt${lang === 'ar' ? ' active' : ''}`}
                      onClick={() => setLang('ar')}
                    >
                      عربي
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="header-user">
            <div className="header-user-avatar" aria-hidden="true">{initials}</div>
            <span className="header-user-name">{displayName}</span>
            <button
              className="header-logout-btn"
              onClick={handleLogout}
              title="Logout"
              aria-label="Logout"
            >
              <i className="fas fa-sign-out-alt"></i>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
